import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc?: string;
  imageAlt: string;
  title: string;
  description?: string;
  gradient?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  imageAlt,
  title,
  description,
  gradient = 'from-[var(--color-electric-blue)] to-[var(--color-violet)]',
  icon: Icon,
}) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Use portal to render modal at document body level
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999]"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3, type: 'spring', damping: 25 }}
            className="fixed inset-4 md:inset-8 lg:inset-16 z-[9999] flex items-center justify-center"
            onClick={onClose}
          >
            <div
              className="relative w-full h-full max-w-6xl max-h-[90vh] bg-[var(--color-dark-navy)] rounded-2xl overflow-hidden shadow-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-white" />
              </button>

              {/* Content */}
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-white/10">
                  <h3 className="text-2xl font-bold text-white">{title}</h3>
                  {description && (
                    <p className="text-[var(--color-light-gray)] mt-1">{description}</p>
                  )}
                </div>

                {/* Image area */}
                <div className="flex-1 p-6 overflow-auto">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={imageAlt}
                      className="w-full h-full object-contain rounded-lg"
                    />
                  ) : (
                    /* Placeholder for when no image is available yet */
                    <div className={`w-full h-full min-h-[400px] rounded-xl bg-gradient-to-br ${gradient} opacity-20 flex flex-col items-center justify-center`}>
                      {Icon && <Icon className="w-24 h-24 text-white/60 mb-4" />}
                      <p className="text-white/60 text-lg font-medium">{imageAlt}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

// Clickable preview component that opens the modal
interface ImagePreviewProps {
  imageSrc?: string;
  imageAlt: string;
  title: string;
  description?: string;
  gradient?: string;
  icon?: React.ComponentType<{ className?: string }>;
  aspectRatio?: 'video' | 'square' | 'wide';
  className?: string;
  children?: React.ReactNode;
}

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  imageSrc,
  imageAlt,
  title,
  description,
  gradient = 'from-[var(--color-electric-blue)] to-[var(--color-violet)]',
  icon: Icon,
  aspectRatio = 'video',
  className = '',
  children,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const aspectClasses = {
    video: 'aspect-video',
    square: 'aspect-square',
    wide: 'aspect-[21/9]',
  };

  return (
    <>
      <div
        onClick={() => setIsModalOpen(true)}
        className={`cursor-pointer group relative ${className}`}
      >
        {children || (
          <div className={`${aspectClasses[aspectRatio]} rounded-xl bg-gradient-to-br ${gradient} opacity-20 flex flex-col items-center justify-center relative overflow-hidden`}>
            {Icon && <Icon className="w-12 h-12 text-white/60 mb-2" />}
            <p className="text-white/60 text-sm">{imageAlt}</p>

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 text-white bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                <MagnifyingGlassPlusIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Click to enlarge</span>
              </div>
            </div>
          </div>
        )}

        {/* Subtle indicator that it's clickable */}
        {!children && (
          <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <MagnifyingGlassPlusIcon className="w-5 h-5 text-white/60" />
          </div>
        )}
      </div>

      <ImageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        title={title}
        description={description}
        gradient={gradient}
        icon={Icon}
      />
    </>
  );
};

export default ImageModal;
